import { Service, TeamMember, Testimonial } from '@/lib/index';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, Clock, DollarSign, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { springPresets, hoverLift } from '@/lib/motion';

interface ServiceCardProps {
  service: Service;
}

export function ServiceCard({ service }: ServiceCardProps) {
  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      transition={springPresets.snappy}
    >
      <Card className="h-full overflow-hidden border-border/50 bg-card/80 backdrop-blur-sm">
        <div className="relative h-64 overflow-hidden">
          <img
            src={service.imageUrl}
            alt={service.title}
            className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="text-2xl font-bold text-foreground">{service.title}</h3>
          </div>
        </div>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground">
              <DollarSign className="h-4 w-4" />
              <span className="font-mono text-sm font-semibold">{service.price}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span className="text-sm">{service.duration}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <CardDescription className="text-base leading-relaxed">
            {service.description}
          </CardDescription>
          <div className="space-y-2">
            <p className="text-sm font-semibold text-foreground">Includes:</p>
            <ul className="space-y-1.5">
              {service.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface TeamCardProps {
  member: TeamMember;
}

export function TeamCard({ member }: TeamCardProps) {
  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      transition={springPresets.snappy}
    >
      <Card className="h-full overflow-hidden border-border/50 bg-card/80 backdrop-blur-sm">
        <div className="relative h-80 overflow-hidden">
          <img
            src={member.imageUrl}
            alt={member.name}
            className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <h3 className="text-2xl font-bold text-foreground">{member.name}</h3>
            <p className="mt-1 font-mono text-sm text-accent">{member.role}</p>
          </div>
        </div>
        <CardContent className="space-y-4 pt-6">
          <p className="text-sm leading-relaxed text-muted-foreground">
            {member.bio}
          </p>
          <div className="space-y-2">
            <p className="text-sm font-semibold text-foreground">Specialties:</p>
            <div className="flex flex-wrap gap-2">
              {member.specialties.map((specialty, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="border-border/50 bg-secondary/50 font-mono text-xs"
                >
                  {specialty}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface TestimonialCardProps {
  testimonial: Testimonial;
}

export function TestimonialCard({ testimonial }: TestimonialCardProps) {
  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      transition={springPresets.snappy}
    >
      <Card className="h-full border-border/50 bg-card/80 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-start gap-4">
            <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full border-2 border-accent/20">
              <img
                src={testimonial.imageUrl}
                alt={testimonial.dogName}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg">{testimonial.clientName}</CardTitle>
              <CardDescription className="mt-1">
                {testimonial.dogName} • {testimonial.dogBreed}
              </CardDescription>
              <div className="mt-2 flex gap-0.5">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star
                    key={index}
                    className={`h-4 w-4 ${
                      index < testimonial.rating
                        ? 'fill-accent text-accent'
                        : 'fill-muted text-muted'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm leading-relaxed text-muted-foreground">
            "{testimonial.comment}"
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}